/**
 * Bir korpusli bug'latgich — turg'un rejim.
 *
 * TENGLAMALAR
 *   Moddiy balans:   F = L + W,   F·x_F = L·x_L           (erigan modda uchmaydi)
 *   Qaynash:         T_b = T_sat(P) + Δ_bpe,  Δ_bpe = k_bpe·x_L
 *   Issiqlik:        Q = U·A·(T_s − T_b),  T_s = T_sat(P_s)
 *   Bug'lanish:      Q·(1 − η_loss) = F·c_p,F·(T_b − T_F) + W·r(T_sat)
 *   Bug' sarfi:      D = Q / r(T_s)  (kondensat to'yingan holda chiqadi)
 *   Iqtisodiylik:    E = W/D;  solishtirma sarf D/W
 *
 * QABUL QILINGAN SODDALASHTIRISHLAR
 *   1. U doimiy (haroratga, konsentratsiyaga bog'liq emas); ifloslanish yo'q.
 *   2. Ikkilamchi bug' r(T_sat) bilan (BPE o'ta qizishi e'tiborsiz; xato < 1 %).
 *   3. Eritma c_p = c_p,erit·(1 − x) + c_p,modda·x (ideal aralashma, erish issiqligi yo'q).
 *   4. BPE chiziqli (k_bpe·x); kuchli elektrolitlar uchun Dühring chiziqlari aniqroq.
 *   5. Gidrostatik depressiya va quvur gidravlikasi yo'q.
 *
 * NOMUVOFIQ REJIMLAR: T_s ≤ T_b — bug'lanish yo'q (critical); barcha erituvchi bug'lansa —
 * quruq qolish (critical); x_L > x_max — kristallanish (warning).
 *
 * QO'LLANISH CHEGARASI: suvli eritmalar, P = 5…200 kPa, P_s = 100…600 kPa.
 * MANBA: Kasatkin, "Osnovnye protsessy i apparaty", 10-bob; Perry's 8th ed., 11-bob
 * (evaporators); McCabe–Smith–Harriott, 16-bob.
 */
import { heatCapacity, heatOfVaporization, saturationTemperature } from '../../properties';
import { bisection } from '../../solvers';
import { warn, type SteadyResult, type Warning } from '../../types';
import type { EvaporatorInputs, EvaporatorOutputs, EvaporatorParams } from './types';

export const DEFAULT_LOSS = 0.03;

export function validateEvaporatorParams(p: EvaporatorParams): void {
  if (!(p.area > 0)) throw new Error('evaporator: area must be > 0');
  if (!(p.U > 0)) throw new Error('evaporator: U must be > 0');
  if (!(p.bpeCoeff >= 0)) throw new Error('evaporator: bpeCoeff must be ≥ 0');
  if (!(p.cpSolute > 0)) throw new Error('evaporator: cpSolute must be > 0');
  if (!(p.xMax > 0 && p.xMax <= 1)) throw new Error('evaporator: xMax must be in (0, 1]');
  if (p.lossFraction !== undefined && !(p.lossFraction >= 0 && p.lossFraction < 1)) throw new Error('evaporator: lossFraction must be in [0, 1)');
  if (p.holdup !== undefined && !(p.holdup > 0)) throw new Error('evaporator: holdup must be > 0');
}

export function validateEvaporatorInputs(i: EvaporatorInputs): void {
  if (!(i.feedFlow >= 0)) throw new Error('evaporator: feedFlow must be ≥ 0');
  if (!(i.feedFraction >= 0 && i.feedFraction < 1)) throw new Error('evaporator: feedFraction must be in [0, 1)');
  if (!(i.feedT > 0)) throw new Error('evaporator: feedT must be > 0');
  if (!(i.steamPressure > 0)) throw new Error('evaporator: steamPressure must be > 0');
  if (!(i.pressure > 0)) throw new Error('evaporator: pressure must be > 0');
}

export function solventOf(p: EvaporatorParams): string {
  return p.solvent ?? 'water';
}

/** Eritma issiqlik sig'imi, J/(kg·K) */
export function solutionCp(p: EvaporatorParams, x: number, T: number, warnings?: Warning[]): number {
  return heatCapacity(solventOf(p), 'liquid', T, warnings) * (1 - x) + p.cpSolute * x;
}

export function boilingPoint(p: EvaporatorParams, Tsat: number, x: number): { Tb: number; bpe: number } {
  const bpe = p.bpeCoeff * x;
  return { Tb: Tsat + bpe, bpe };
}

/**
 * Berilgan konsentratsiya x (kamera) uchun oniy balans: Q, W (kg/s), Tb. Dinamik rejimda
 * ham ishlatiladi (x — kameradagi joriy konsentratsiya).
 */
export function instantBalance(p: EvaporatorParams, i: EvaporatorInputs, x: number, warnings: Warning[]) {
  const s = solventOf(p);
  const Tsat = saturationTemperature(s, i.pressure, warnings);
  const Tsteam = saturationTemperature(s, i.steamPressure, warnings);
  const { Tb, bpe } = boilingPoint(p, Tsat, x);
  const r = heatOfVaporization(s, Tsat, warnings);
  const rs = heatOfVaporization(s, Tsteam, warnings);
  const loss = p.lossFraction ?? DEFAULT_LOSS;
  const deltaT = Tsteam - Tb;
  const Q = Math.max(0, p.U * p.area * deltaT);
  const cpF = solutionCp(p, i.feedFraction, 0.5 * (i.feedT + Tb), warnings);
  const sensible = i.feedFlow * cpF * (Tb - i.feedT);
  const W = Math.max(0, (Q * (1 - loss) - sensible) / r);
  return { Tsat, Tsteam, Tb, bpe, r, rs, deltaT, Q, W, cpF, loss };
}

export function solveSteady(p: EvaporatorParams, i: EvaporatorInputs): SteadyResult<EvaporatorOutputs> {
  validateEvaporatorParams(p);
  validateEvaporatorInputs(i);
  const warnings: Warning[] = [];
  const F = i.feedFlow;
  const xF = i.feedFraction;
  // W ga nisbatan qoldiq: g(W) = Q(T_b(x_L(W)))·(1−loss) − sezgir − W·r; x_L = F x_F/(F − W)
  const xAt = (W: number) => (F - W > 1e-12 ? Math.min(0.999, (F * xF) / (F - W)) : 0.999);
  const g = (W: number) => {
    const b = instantBalance(p, i, xAt(W), []);
    return b.Q * (1 - b.loss) - F * b.cpF * (b.Tb - i.feedT) - W * b.r;
  };
  const Wmax = Math.max(0, F * (1 - xF) * 0.999);
  let W = 0;
  let converged = true;
  let iterations = 0;
  let residual = 0;
  const g0 = g(0);
  if (F <= 0 || g0 <= 0) {
    W = 0;
  } else if (g(Wmax) > 0) {
    W = Wmax; // barcha erituvchi bug'landi — quruq qolish
  } else {
    const r = bisection(g, 0, Wmax, { xtol: 1e-9 * Math.max(F, 1e-6), ftol: 1e-6 });
    W = r.value;
    converged = r.converged;
    iterations = r.iterations;
    residual = r.residual;
  }
  const xL = xAt(W);
  const b = instantBalance(p, i, xL, warnings);
  const L = F - W;
  if (b.deltaT <= 0) warn(warnings, 'evap.noEvaporation', 'critical', { Tsteam: Math.round(b.Tsteam), Tb: Math.round(b.Tb) });
  else if (b.deltaT < 5) warn(warnings, 'evap.lowDeltaT', 'info', { deltaT: Math.round(b.deltaT * 10) / 10 });
  if (F > 0 && W >= Wmax - 1e-12 && Wmax > 0) warn(warnings, 'evap.dryOut', 'critical');
  if (xL > p.xMax) warn(warnings, 'evap.crystallization', 'warning', { xL: Math.round(xL * 1000) / 1000, xMax: p.xMax });
  const D = b.Q / b.rs;
  const outputs: EvaporatorOutputs = {
    t: 0,
    W,
    L,
    xL,
    Tb: b.Tb,
    Tsat: b.Tsat,
    bpe: b.bpe,
    Tsteam: b.Tsteam,
    deltaT: b.deltaT,
    Q: b.Q,
    D,
    economy: D > 0 ? W / D : 0,
    specificSteam: W > 0 ? D / W : Infinity,
    r: b.r,
    M: p.holdup ?? 0,
  };
  return { outputs, warnings, diagnostics: { converged, iterations, residual } };
}
